// pages/AnimeListPage.tsx
import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import api from '../api/axios';
import '../styles/AnimeListPage.css';
import { useClickable } from '../providers/ClickableProvider';
import {Check, RefreshCw, ChevronDown, List, Star, LayoutList} from 'lucide-react';

type DropdownType = 'animeStatus' | 'season' | 'genres' | 'ageRating' | null;
type ViewMode = 'table' | 'card';
type DisplayState = 'initial' | 'loading' | 'no-data' | 'no-results' | 'data' | 'error';

interface Anime {
    id: number;
    title: string;
    imageUrl: string;
    episodes: number;
    rating?: number;
    releaseYear?: number;
    season?: string;
    status?: string;
    description?: string;
    genres?: string[];
}

interface Genre {
    id: number;
    name: string;
}

const AnimeListPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [filteredList, setFilteredList] = useState<Anime[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [genres, setGenres] = useState<Genre[]>([]);
    const [genresLoading, setGenresLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { makeClickable } = useClickable();
    const [displayState, setDisplayState] = useState<DisplayState>('initial')
    const [openDropdown, setOpenDropdown] = useState<DropdownType>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });
    const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isDataStale, setIsDataStale] = useState(false);
    const [combinedList, setCombinedList] = useState<Anime[]>([]); // Все загруженные аниме
    const [hasMore, setHasMore] = useState(true); // Есть ли еще аниме для загрузки
    const [currentPage, setCurrentPage] = useState(0); // Текущая страница
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const observerTargetRef = useRef<HTMLDivElement>(null);
    const [ageRatings, setAgeRatings] = useState<Array<{value: string, label: string}>>([]);
    const [ageRatingsLoading, setAgeRatingsLoading] = useState(false);

    const fetchAgeRatings = useCallback(async () => {
        try {
            setAgeRatingsLoading(true);
            const response = await api.get('/age-ratings'); // Изменили URL
            console.log('Age ratings response:', response.data);

            // Преобразуем данные из нового формата
            const formattedData = response.data.map((item: any) => ({
                value: item.code,
                label: item.description
            }));

            console.log('Formatted data:', formattedData);
            setAgeRatings(formattedData);
        } catch (error) {
            console.error('Error fetching age ratings:', error);
        } finally {
            setAgeRatingsLoading(false);
        }
    }, []);

    const formatAgeRating = (rating: string): string => {
        if (!rating) return '';

        // Создаем карту для преобразования
        const ratingMap: { [key: string]: string } = {
            'PG_13': 'PG-13',
            'R_17': 'R-17',
            'R_PLUS': 'R+'
        };

        // Если есть в карте - возвращаем преобразованное значение
        if (ratingMap[rating]) {
            return ratingMap[rating];
        }

        // Для G и PG возвращаем как есть
        return rating;
    };

    useEffect(() => {
        fetchAgeRatings();
    }, [fetchAgeRatings]);

    const dropdownRefs = {
        animeStatus: useRef<HTMLDivElement>(null),
        season: useRef<HTMLDivElement>(null),
        genres: useRef<HTMLDivElement>(null),
        ageRating: useRef<HTMLDivElement>(null)
    };

    useEffect(() => {
        return () => {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                openDropdown &&
                dropdownRefs[openDropdown].current &&
                !dropdownRefs[openDropdown].current?.contains(event.target as Node)
            ) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdown]);

    const [filters, setFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    const [manualFilters, setManualFilters] = useState({
        title: '',
        animeStatus: '',
        releaseYear: '',
        season: '',
        genres: [] as string[],
        globalRating: '',
        globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
        episodes: '',
        episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
        ageRating: ''
    });

    const fetchGenres = useCallback(async () => {
        try {
            setGenresLoading(true);
            const response = await api.get('/genres');
            const sortedGenres = response.data.sort((a: Genre, b: Genre) =>
                a.name.localeCompare(b.name)
            );
            setGenres(sortedGenres);
        } catch (error) {
            console.error('Error fetching genres:', error);
        } finally {
            setGenresLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGenres();
    }, [fetchGenres]);

    const applyFilters = useCallback(async (filterCriteria: typeof filters, resetPage: boolean = true) => {
        try {
            setIsApplyingFilters(true);

            if (resetPage) {
                setCombinedList([]);
                setCurrentPage(0);
                setHasMore(true);
            }

            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
            }

            loadingTimerRef.current = setTimeout(() => {
                if (resetPage) {
                    setDisplayState('loading');
                } else {
                    setIsLoadingMore(true);
                }
            }, resetPage ? 700 : 300);

            const params = new URLSearchParams();

            if (filterCriteria.title.trim()) params.append('title', filterCriteria.title.trim());
            if (filterCriteria.animeStatus) params.append('status', filterCriteria.animeStatus);
            if (filterCriteria.releaseYear) params.append('year', filterCriteria.releaseYear);
            if (filterCriteria.season) params.append('season', filterCriteria.season);
            if (filterCriteria.ageRating) params.append('ageRating', filterCriteria.ageRating);

            if (filterCriteria.genres.length > 0) {
                filterCriteria.genres.forEach(genre => params.append('genre', genre));
            }

            if (filterCriteria.globalRating && filterCriteria.globalRating !== '') {
                params.append('rating', filterCriteria.globalRating);
                params.append('globalRatingCompare', filterCriteria.globalRatingCompare);
            }

            if (filterCriteria.episodes && filterCriteria.episodes !== '') {
                params.append('episodes', filterCriteria.episodes);
                params.append('episodesCompare', filterCriteria.episodesCompare);
            }

            const pageToLoad = resetPage ? 0 : currentPage + 1;
            params.append('page', pageToLoad.toString());
            params.append('size', '15');

            console.log('Loading page:', pageToLoad, 'with filters:', params.toString());

            const response = await api.get(`/anime/search?${params.toString()}`);

            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }

            let animeItems: any[] = [];
            let hasMoreData = true;

            if (response.data) {
                if (response.data.content && Array.isArray(response.data.content)) {
                    animeItems = response.data.content;
                    hasMoreData = !response.data.last;
                } else if (Array.isArray(response.data)) {
                    animeItems = response.data;
                    hasMoreData = response.data.length === 15;
                }
            }

            // ВАЖНО: УБРАЛИ ОТДЕЛЬНЫЕ ЗАПРОСЫ!
            const newData = animeItems.map((anime: any) => {
                return {
                    id: anime.id,
                    title: anime.title || 'Без названия',
                    imageUrl: anime.imageUrl || '/images/default/poster.jpg',
                    episodes: anime.episodes || 0,
                    rating: anime.rating || 0,
                    // ВСЕ ЭТИ ДАННЫЕ ДОЛЖНЫ ПРИХОДИТЬ С БЭКЕНДА!
                    releaseYear: anime.releaseDate ? new Date(anime.releaseDate).getFullYear() : undefined,
                    season: anime.season,
                    status: anime.animeStatus,
                    description: anime.description || 'Описание отсутствует',
                    genres: anime.genres || []
                };
            });

            if (resetPage) {
                setCombinedList(newData);
                setHasMore(hasMoreData);
                setCurrentPage(0);

                if (newData.length === 0) {
                    const hasFilters = filterCriteria.title ||
                        filterCriteria.animeStatus ||
                        filterCriteria.releaseYear ||
                        filterCriteria.season ||
                        filterCriteria.genres.length > 0 ||
                        filterCriteria.globalRating ||
                        filterCriteria.episodes;
                    setDisplayState(hasFilters ? 'no-results' : 'no-data');
                } else {
                    setDisplayState('data');
                }
            } else {
                setCombinedList(prev => [...prev, ...newData]);
                setHasMore(hasMoreData);
                setCurrentPage(pageToLoad);
            }

            setError(null);
            setIsLoadingMore(false);
            setIsApplyingFilters(false);

        } catch (err: any) {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }

            if (resetPage) {
                setDisplayState('error');
                setError('Произошла ошибка при загрузке данных');
            } else {
                setIsLoadingMore(false);
            }
            setIsApplyingFilters(false);
            console.error('Error applying filters:', err);
        }
    }, [currentPage]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                setIsLoading(true);

                const filtersFromUrl = {
                    title: searchParams.get('title') || '',
                    animeStatus: searchParams.get('animeStatus') || '',
                    releaseYear: searchParams.get('releaseYear') || '',
                    season: searchParams.get('season') || '',
                    genres: searchParams.getAll('genre') || [],
                    globalRating: searchParams.get('globalRating') || '',
                    globalRatingCompare: (searchParams.get('globalRatingCompare') as 'gte' | 'lte' | 'eq') || 'eq',
                    episodes: searchParams.get('episodes') || '',
                    episodesCompare: (searchParams.get('episodesCompare') as 'gte' | 'lte' | 'eq') || 'eq',
                    ageRating: searchParams.get('ageRating') || ''
                };

                if (filtersFromUrl.title) {
                    const cleanTitle = filtersFromUrl.title.replace(/\s+/g, '');
                    if (cleanTitle.length < 2) {
                        setError("Введите минимум 2 символа для поиска");
                        setDisplayState('error');
                        return;
                    }
                }

                setFilters(filtersFromUrl);
                setManualFilters(filtersFromUrl);
                setAppliedFilters(filtersFromUrl);

                // Загружаем первую страницу
                await applyFilters(filtersFromUrl, true);

            } catch (error) {
                console.error('Error initializing:', error);
                setError('Ошибка при загрузке данных');
                setDisplayState('error');
            } finally {
                setIsLoading(false);
            }
        };

        initializeData();
    }, [searchParams]);

    const loadMoreAnime = useCallback(async () => {
        if (isLoadingMore || !hasMore || isApplyingFilters) return;

        console.log('Loading more anime, current page:', currentPage);
        await applyFilters(filters, false);
    }, [isLoadingMore, hasMore, isApplyingFilters, filters, applyFilters, currentPage]);

    const InfiniteScrollIndicator = ({ viewMode }: { viewMode: ViewMode }) => {
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!ref.current) return;

            const observer = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isApplyingFilters) {
                        console.log('Infinite scroll triggered for', viewMode);
                        loadMoreAnime();
                    }
                },
                {
                    root: null,
                    rootMargin: '100px',
                    threshold: 0.1
                }
            );

            observer.observe(ref.current);

            return () => {
                observer.disconnect();
            };
        }, [hasMore, isLoadingMore, isApplyingFilters, loadMoreAnime, viewMode]);

        return (
            <>
                <div ref={ref} style={{ height: '1px' }} />

                {isLoadingMore && (
                    <div className="anime-page-loading-more">
                        <div className="anime-page-loading-spinner"></div>
                        <span>Загрузка аниме...</span>
                    </div>
                )}
            </>
        );
    };

    const toggleDropdown = (type: DropdownType) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const handleAnimeStatusSelect = (status: string) => {
        setManualFilters(prev => ({...prev, animeStatus: status}));
        setOpenDropdown(null);
    };

    const handleSeasonSelect = (season: string) => {
        setManualFilters(prev => ({...prev, season}));
        setOpenDropdown(null);
    };

    const handleGenreToggle = (genreName: string) => {
        const newGenres = manualFilters.genres.includes(genreName)
            ? manualFilters.genres.filter(g => g !== genreName)
            : [...manualFilters.genres, genreName];
        setManualFilters(prev => ({...prev, genres: newGenres}));
    };

    const getAnimeStatusLabel = (status?: string) => {
        if (!status) return 'Все статусы';

        switch (status) {
            case 'ONGOING':
                return 'Онгоинг';
            case 'COMPLETED':
                return 'Вышел';
            case 'UPCOMING':
                return 'Анонсирован';
            default:
                return status;
        }
    };

    const getSeasonLabel = (season?: string) => {
        if (!season) return 'Любой сезон';
        switch (season) {
            case 'WINTER':
                return 'Зима';
            case 'SPRING':
                return 'Весна';
            case 'SUMMER':
                return 'Лето';
            case 'FALL':
                return 'Осень';
            default:
                return season;
        }
    };

    const updateURL = useCallback((newFilters: typeof filters) => {
        const params = new URLSearchParams();

        if (newFilters.title) params.set('title', newFilters.title);
        if (newFilters.animeStatus) params.set('animeStatus', newFilters.animeStatus);
        if (newFilters.releaseYear) params.set('releaseYear', newFilters.releaseYear);
        if (newFilters.season) params.set('season', newFilters.season);
        if (newFilters.ageRating) params.set('ageRating', newFilters.ageRating);
        if (newFilters.globalRating) {
            params.set('globalRating', newFilters.globalRating);
            params.set('globalRatingCompare', newFilters.globalRatingCompare);
        }
        if (newFilters.episodes) {
            params.set('episodes', newFilters.episodes);
            params.set('episodesCompare', newFilters.episodesCompare);
        }

        newFilters.genres.forEach(genre => {
            params.append('genre', genre);
        });

        setSearchParams(params);
    }, [setSearchParams]);

    const handleSearch = async () => {
        if (manualFilters.title) {
            const cleanTitle = manualFilters.title.replace(/\s+/g, '');
            if (cleanTitle.length < 2) {
                setError("Введите минимум 2 символа для поиска");
                setDisplayState('error');
                return;
            }
        }

        setFilters(manualFilters);
        setAppliedFilters(manualFilters);
        updateURL(manualFilters);

        setIsDataStale(true);
        await applyFilters(manualFilters, true); // true = сбросить страницу
        setIsDataStale(false);
    };

    const handleResetFilters = async () => {
        const resetFilters = {
            title: '',
            animeStatus: '',
            releaseYear: '',
            season: '',
            genres: [] as string[],
            globalRating: '',
            globalRatingCompare: 'eq' as 'gte' | 'lte' | 'eq',
            episodes: '',
            episodesCompare: 'eq' as 'gte' | 'lte' | 'eq',
            ageRating: ''
        };

        setFilters(resetFilters);
        setManualFilters(resetFilters);
        setAppliedFilters(resetFilters);
        updateURL(resetFilters);

        setIsDataStale(true);
        await applyFilters(resetFilters, true); // true = сбросить страницу
        setIsDataStale(false);
    };

    const handleFilterChange = (key: keyof typeof manualFilters, value: string) => {
        setManualFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const getHasFilters = useMemo(() => {
        return filters.title ||
            filters.animeStatus ||
            filters.releaseYear ||
            filters.season ||
            filters.genres.length > 0 ||
            filters.globalRating ||
            filters.episodes;
    }, [filters]);

    const currentData = useMemo(() => {
        if (isDataStale || isApplyingFilters) {
            return combinedList; // Возвращаем то, что уже загружено
        }
        return combinedList;
    }, [combinedList, isApplyingFilters, isDataStale]);

    const getApplyButtonTitle = useMemo(() => {
        // Сначала проверяем заполненность полей с операторами сравнения
        const hasRatingOperator = manualFilters.globalRatingCompare !== 'eq';
        const hasEpisodesOperator = manualFilters.episodesCompare !== 'eq';

        if (hasRatingOperator && !manualFilters.globalRating) {
            return "Для сравнения оценки введите число или верните фильтр в исходное состояние (=)";
        }

        if (hasEpisodesOperator && !manualFilters.episodes) {
            return "Для сравнения эпизодов введите число или верните фильтр в исходное состояние (=)";
        }

        // Проверяем что фильтры изменились
        const filtersChanged = JSON.stringify(manualFilters) !== JSON.stringify(appliedFilters);

        if (!filtersChanged) {
            return "Нет изменений в фильтрах";
        }

        return "Применить фильтры";
    }, [manualFilters, appliedFilters]);

    const hasFilterChanges = useMemo(() => {
        // Сначала проверяем что фильтры вообще изменились
        const filtersChanged = JSON.stringify(manualFilters) !== JSON.stringify(appliedFilters);

        if (!filtersChanged) return false;

        // Проверяем заполненность полей с операторами сравнения
        const hasRatingOperator = manualFilters.globalRatingCompare !== 'eq';
        const hasEpisodesOperator = manualFilters.episodesCompare !== 'eq';

        // Если выбрано сравнение (≥ или ≤), но не введено число - нельзя применять
        if ((hasRatingOperator && !manualFilters.globalRating) ||
            (hasEpisodesOperator && !manualFilters.episodes)) {
            return false;
        }

        // Дополнительно можно проверить корректность чисел
        if (manualFilters.globalRating) {
            const rating = parseFloat(manualFilters.globalRating);
            if (isNaN(rating) || rating < 1 || rating > 10) {
                return false;
            }
        }

        if (manualFilters.episodes) {
            const episodes = parseInt(manualFilters.episodes);
            if (isNaN(episodes) || episodes < 1 || episodes > 1000) {
                return false;
            }
        }

        if (manualFilters.ageRating) {
            // Проверка что ageRating это допустимое значение из списка
            const validAgeRatings = ageRatings.map(r => r.value);
            if (!validAgeRatings.includes(manualFilters.ageRating) && manualFilters.ageRating !== '') {
                return false;
            }
        }

        return true;
    }, [manualFilters, appliedFilters]);

    const hasAppliedFilters = useMemo(() => {
        return appliedFilters.title ||
            appliedFilters.animeStatus ||
            appliedFilters.releaseYear ||
            appliedFilters.season ||
            appliedFilters.genres.length > 0 ||
            appliedFilters.globalRating ||
            appliedFilters.episodes ||
            appliedFilters.ageRating;
    }, [appliedFilters]);

    const toggleViewMode = () => {
        setViewMode(prev => prev === 'table' ? 'card' : 'table');
    };

    const truncateDescription = (description?: string, maxLines: number = 3) => {
        if (!description || description === 'Описание отсутствует') {
            return 'Описание отсутствует';
        }

        const words = description.split(' ');
        const maxWords = maxLines * 15;
        if (words.length > maxWords) {
            return words.slice(0, maxWords).join(' ') + '...';
        }
        return description;
    };

    const getAnimeGenresText = (anime: Anime) => {
        if (anime.genres && anime.genres.length > 0) {
            return anime.genres.slice(0, 3).join(', ');
        }
        return 'Жанры не указаны';
    };

    const renderRating = (rating?: number) => {
        const ratingValue = rating || 0;
        return (
            <div className="anime-page-rating-badge">
                <Star size={13} color="var(--text-rating)"/>
                <span className="rating-page-value-profile">{ratingValue.toFixed(1)}</span>
            </div>
        );
    };

    // Функция для рендеринга состояния
    const renderState = () => {
        switch (displayState) {
            case 'initial':
                return null;
            case 'loading':
                return (
                    <div className="anime-page-no-results">
                        <div style={{textAlign: 'center'}}>
                            Загрузка аниме...
                        </div>
                    </div>
                );
            case 'no-data':
                return (
                    <div className="anime-page-no-results">
                        <div style={{textAlign: 'center'}}>
                            Нет аниме для отображения
                        </div>
                    </div>
                );
            case 'no-results':
                return (
                    <div className="anime-page-no-results">
                        <div style={{textAlign: 'center'}}>
                            Аниме не найдено
                        </div>
                    </div>
                );
            case 'error':
                return (
                    <div className="anime-page-error">
                        {error || 'Произошла ошибка'}
                    </div>
                );
            case 'data':
                return (
                    <>
                        {viewMode === 'table' ? renderTableView() : renderCardView()}
                        <InfiniteScrollIndicator viewMode={viewMode} />
                    </>
                );
        }
    };

    const renderTableView = () => (
        <>
            <div className="anime-page-table-headers">
                <div className="anime-page-header-number">№</div>
                <div className="anime-page-header-image">img</div>
                <div className="anime-page-header-title">Название</div>
                <div className="anime-page-header-global-rating">Общая оценка</div>
                <div className="anime-page-header-episodes">Эпизоды</div>
            </div>

            <div className="anime-page-items">
                {currentData.map((anime, index) => (
                    <div key={`anime-${anime.id}-${index}-${anime.title}`} className="anime-page-item">
                        <div className="anime-page-item-number">{index + 1}</div>
                        <div className="anime-page-item-image">
                            <div
                                className="clickable-image-wrapper"
                                {...makeClickable(`/anime/${anime.id}`, (e) => {
                                    if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                        navigate(`/anime/${anime.id}`);
                                    }
                                })}
                                style={{cursor: 'pointer'}}
                            >
                                <img
                                    src={anime.imageUrl}
                                    alt={anime.title}
                                    className="anime-page-image"
                                    onError={(e) => {
                                        e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=50&h=70&fit=crop";
                                    }}
                                />
                            </div>
                        </div>
                        <div className="anime-page-item-title">
                        <span
                            className="anime-page-name clickable-name"
                            {...makeClickable(`/anime/${anime.id}`, (e) => {
                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                    navigate(`/anime/${anime.id}`);
                                }
                            })}
                            style={{cursor: 'pointer'}}
                        >
                            {anime.title}
                        </span>
                        </div>
                        <div className="anime-page-item-global-rating">
                            {anime.rating ? anime.rating.toFixed(1) : '0'}
                        </div>
                        <div className="anime-page-item-episodes">
                            {anime.episodes || '?'}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );

// Выносим карточный вид в отдельную функцию
    const renderCardView = () => (
        <div className="anime-page-card-view-container">
            {currentData.map((anime, index) => (
                <div key={`anime-card-${anime.id}-${index}-${anime.title}`} className="anime-page-card-item">
                    <div className="anime-page-card-item-left">
                        <div
                            className="clickable-image-wrapper"
                            {...makeClickable(`/anime/${anime.id}`, (e) => {
                                if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                    navigate(`/anime/${anime.id}`);
                                }
                            })}
                            style={{cursor: 'pointer'}}
                        >
                            <img
                                src={anime.imageUrl}
                                alt={anime.title}
                                className="anime-page-card-item-img"
                                onError={(e) => {
                                    e.currentTarget.src = "https://images.unsplash.com/photo-1578632767115-351597cf2477?w=150&h=210&fit=crop";
                                }}
                            />
                        </div>
                    </div>
                    <div className="anime-page-card-item-right">
                        <div className="anime-page-card-title-grid">
                            <div className="anime-page-card-title-wrapper">
                            <span
                                className="anime-page-card-item-title clickable-title"
                                {...makeClickable(`/anime/${anime.id}`, (e) => {
                                    if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                        navigate(`/anime/${anime.id}`);
                                    }
                                })}
                                style={{cursor: 'pointer'}}
                            >
                                {anime.title}
                            </span>
                            </div>
                            <div className="anime-page-card-rating">
                                {renderRating(anime.rating)}
                            </div>
                        </div>

                        <div className="anime-page-card-item-meta">
                        <span className="anime-page-card-item-status">
                            {getAnimeStatusLabel(anime.status)}
                        </span>
                            <span className="anime-page-card-item-divider">/</span>
                            <span className="anime-page-card-item-year">
                            {anime.releaseYear || 'Неизвестно'}
                        </span>
                            <span className="anime-page-card-item-divider">/</span>
                            <span className="anime-page-card-item-genres">
                            {getAnimeGenresText(anime)}
                        </span>
                        </div>

                        <div className="anime-page-card-item-description">
                            {truncateDescription(anime.description, 3)}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="anime-page">
            <div className="anime-page-container-wrapper">
                <div className="anime-page-container">
                    <div className="anime-page-section">
                        <div className="anime-page-list-header">
                            <h2 className="anime-page-list-title">Список аниме</h2>
                            <button
                                className="anime-page-view-mode-btn"
                                onClick={toggleViewMode}
                                title={viewMode === 'table' ? 'Показать карточки' : 'Показать таблицу'}
                            >
                                {viewMode === 'table' ? <LayoutList size={24}/> : <List size={24}/>}
                            </button>
                        </div>

                        <div className="anime-page-list">
                            {renderState()}
                        </div>
                    </div>

                    <div className="anime-page-filters-panel">
                        <h3 className="anime-page-filters-title">Фильтры</h3>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Название</label>
                            <input
                                type="text"
                                value={manualFilters.title}
                                onChange={(e) => handleFilterChange('title', e.target.value)}
                                className="anime-page-filter-input"
                                placeholder="Поиск..."
                            />
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Статус аниме</label>
                            <div className="anime-page-dropdown-container-list" ref={dropdownRefs.animeStatus}>
                                <button
                                    className={`anime-page-dropdown-button ${openDropdown === 'animeStatus' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('animeStatus')}
                                >
                                    <span>{getAnimeStatusLabel(manualFilters.animeStatus)}</span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'animeStatus' && (
                                    <div className="anime-page-dropdown-menu-list">
                                        <div className="anime-page-dropdown-option selected"
                                             onClick={() => handleAnimeStatusSelect('')}>
                                            Все статусы
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('ONGOING')}>
                                            Онгоинг
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('COMPLETED')}>
                                            Вышел
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleAnimeStatusSelect('UPCOMING')}>
                                            Анонсирован
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Год выпуска</label>
                            <input
                                type="number"
                                value={manualFilters.releaseYear}
                                onChange={(e) => handleFilterChange('releaseYear', e.target.value)}
                                className="anime-page-filter-input"
                                placeholder="Год"
                                min="1950"
                                max={new Date().getFullYear() + 2}
                            />
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Сезон</label>
                            <div className="anime-page-dropdown-container-list" ref={dropdownRefs.season}>
                                <button
                                    className={`anime-page-dropdown-button ${openDropdown === 'season' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('season')}
                                >
                                    <span>{getSeasonLabel(manualFilters.season)}</span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'season' && (
                                    <div className="anime-page-dropdown-menu-list">
                                        <div className="anime-page-dropdown-option selected"
                                             onClick={() => handleSeasonSelect('')}>
                                            Любой сезон
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleSeasonSelect('WINTER')}>
                                            Зима
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleSeasonSelect('SPRING')}>
                                            Весна
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleSeasonSelect('SUMMER')}>
                                            Лето
                                        </div>
                                        <div className="anime-page-dropdown-option"
                                             onClick={() => handleSeasonSelect('FALL')}>
                                            Осень
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Жанры</label>
                            <div className="anime-page-dropdown-container-list" ref={dropdownRefs.genres}>
                                <button
                                    className={`anime-page-dropdown-button ${openDropdown === 'genres' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('genres')}
                                >
                                    <span>
                                        {manualFilters.genres.length === 0
                                            ? 'Все жанры'
                                            : `Выбрано: ${manualFilters.genres.length}`}
                                    </span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'genres' && (
                                    <div className="anime-page-dropdown-menu-list">
                                        <div className="anime-page-genres-dropdown-content">
                                            {genresLoading ? (
                                                <div style={{
                                                    color: 'rgba(255,255,255,0.7)',
                                                    textAlign: 'center',
                                                    padding: '10px'
                                                }}>
                                                    Загрузка жанров...
                                                </div>
                                            ) : (
                                                genres.map(genre => (
                                                    <div
                                                        key={genre.id}
                                                        className={`anime-page-genre-item ${manualFilters.genres.includes(genre.name) ? 'selected' : ''}`}
                                                        onClick={() => handleGenreToggle(genre.name)}
                                                    >
                                                        <span className="anime-page-genre-item-text">{genre.name}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Рейтинг</label>
                            <div className="anime-page-rating-dropdown-container" ref={dropdownRefs.ageRating}>
                                <button
                                    className={`anime-page-rating-dropdown-button ${openDropdown === 'ageRating' ? 'open' : ''}`}
                                    onClick={() => toggleDropdown('ageRating')}
                                >
                                    <span>
                                        {manualFilters.ageRating === ''
                                            ? 'Все рейтинги'
                                            : formatAgeRating(manualFilters.ageRating)}
                                    </span>
                                    <ChevronDown className="chevron" size={16}/>
                                </button>
                                {openDropdown === 'ageRating' && (
                                    <div className="anime-page-rating-dropdown-menu">
                                        <div
                                            className={`anime-page-rating-dropdown-option ${manualFilters.ageRating === '' ? 'selected' : ''}`}
                                            onClick={() => {
                                                handleFilterChange('ageRating', '');
                                                setOpenDropdown(null);
                                            }}
                                        >
                                            Все рейтинги
                                        </div>
                                        {ageRatingsLoading ? (
                                            <div className="anime-page-rating-dropdown-option"
                                                 style={{color: 'rgba(255,255,255,0.7)', textAlign: 'center'}}>
                                                Загрузка...
                                            </div>
                                        ) : (
                                            ageRatings.map(rating => (
                                                <div
                                                    key={rating.value}
                                                    className={`anime-page-rating-dropdown-option ${manualFilters.ageRating === rating.value ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handleFilterChange('ageRating', rating.value);
                                                        setOpenDropdown(null);
                                                    }}
                                                >
                                                    {formatAgeRating(rating.value)}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Общая оценка</label>
                            <div className="anime-page-compact-filter-with-select">
                                <select
                                    value={manualFilters.globalRatingCompare}
                                    onChange={(e) => {
                                        handleFilterChange('globalRatingCompare', e.target.value)
                                    }}
                                    className="anime-page-comparison-select"
                                >
                                    <option value="eq">=</option>
                                    <option value="gte">≥</option>
                                    <option value="lte">≤</option>
                                </select>
                                <input
                                    type="number"
                                    value={manualFilters.globalRating}
                                    onChange={(e) => handleFilterChange('globalRating', e.target.value)}
                                    className="anime-page-compact-input-with-select"
                                    placeholder="1-10"
                                    min="1"
                                    max="10"
                                    step="0.1"
                                />
                            </div>
                        </div>

                        <div className="anime-page-filter-group">
                            <label className="anime-page-filter-label">Эпизоды</label>
                            <div className="anime-page-compact-filter-with-select">
                                <select
                                    value={manualFilters.episodesCompare}
                                    onChange={(e) => handleFilterChange('episodesCompare', e.target.value as 'eq' | 'gte' | 'lte')}
                                    className="anime-page-comparison-select"
                                >
                                    <option value="eq">=</option>
                                    <option value="gte">≥</option>
                                    <option value="lte">≤</option>
                                </select>
                                <input
                                    type="number"
                                    value={manualFilters.episodes}
                                    onChange={(e) => handleFilterChange('episodes', e.target.value)}
                                    className="anime-page-compact-input-with-select"
                                    placeholder="1-1000"
                                    min="1"
                                    max="1000"
                                />
                            </div>
                        </div>

                        <div className="anime-page-filter-actions">
                            <button
                                onClick={handleSearch}
                                className="anime-page-filter-btn anime-page-apply-btn"
                                disabled={!hasFilterChanges || isApplyingFilters}
                                title={getApplyButtonTitle}
                            >
                                <Check className="anime-page-filter-btn-icon" size={16}/>
                            </button>
                            <button
                                onClick={handleResetFilters}
                                className="anime-page-filter-btn anime-page-reset-btn"
                                disabled={!hasAppliedFilters || isApplyingFilters}
                                title={!hasAppliedFilters ? "Нет примененных фильтров" : "Сбросить фильтры"}
                            >
                                <RefreshCw className="anime-page-filter-btn-icon" size={16}/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AnimeListPage;